/**
 * Mio design tokens (Wave 1 of the extraordinary-modern UI design).
 *
 * Single source of truth for typography sizing, motion durations,
 * stagger delays, and component-level constants. Components import
 * from here instead of hard-coding magic numbers.
 *
 * Color tokens live in `app/globals.css` as HSL custom properties so
 * Tailwind utilities (`bg-background`, `text-accent`, etc.) keep
 * working. This file does NOT redeclare colors - it complements them
 * with non-color tokens.
 *
 * See `docs/agent-ux-design.md` for the design rationale.
 */

// ---------- Typography sizing -------------------------------------------

/**
 * Hero TFP number sizing. The True Final Price is THE hero element
 * on every comparison card; it uses display-weight Geist Sans with
 * tight tracking. These values are large by design.
 */
export const TYPE_HERO_PRICE = {
  desktop: "text-7xl sm:text-8xl",  // ~72-96px
  mobile: "text-5xl sm:text-6xl",   // ~48-60px
  weight: "font-bold",              // Geist Sans weight 700
  tracking: "tracking-tight",       // tight letter-spacing
  numerics: "tabular-nums",         // monospaced digits for prices
} as const;

/** Section / page heading. Used on the comparison page header. */
export const TYPE_DISPLAY = {
  desktop: "text-3xl sm:text-4xl",
  mobile: "text-2xl",
  weight: "font-semibold",
  tracking: "tracking-tight",
} as const;

/** Body copy in the chat thread. */
export const TYPE_BODY = {
  size: "text-[15px]",          // slightly larger than default text-sm
  leading: "leading-relaxed",   // generous line-height for chat readability
} as const;

/** Small / caption text — disclosures, retailer subtext, timestamps. */
export const TYPE_CAPTION = {
  size: "text-xs",
  weight: "font-medium",
  color: "text-muted-foreground",
} as const;

// ---------- Motion ------------------------------------------------------

/**
 * All durations in milliseconds. Used directly with Framer Motion's
 * `transition.duration` (which expects seconds) by dividing by 1000;
 * a helper `ms()` is exported below.
 *
 * Respect `prefers-reduced-motion` at the consumer site by passing
 * `transition: { duration: 0 }` when the media query matches.
 */
export const MOTION = {
  /** Fast UI response (hover, focus, button press). */
  instant: 80,
  /** Tooltip, dropdown, small element transitions. */
  fast: 120,
  /** Default. Page transitions, message arrival, chip appear. */
  base: 180,
  /** Comparison card reveal, panel expand. Slightly weighty. */
  smooth: 240,
  /** Hero / first-impression reveals. Use sparingly. */
  emphatic: 320,
} as const;

/** Stagger delay between children of the same animated group. */
export const STAGGER = {
  /** Refinement chips appearing one after another. */
  chips: 60,
  /** Retailer rows revealing in the comparison card. */
  rows: 50,
  /** Message bubbles when multiple appear at once. */
  messages: 80,
} as const;

/** Spring presets — Linear-style smooth springs, no bouncing. */
export const SPRING = {
  smooth: { type: "spring" as const, stiffness: 240, damping: 28 },
  snappy: { type: "spring" as const, stiffness: 360, damping: 30 },
  /** Sub-element use; minimal motion for refined feel. */
  subtle: { type: "spring" as const, stiffness: 200, damping: 32 },
} as const;

/** Convert MOTION milliseconds to Framer Motion seconds. */
export function ms(milliseconds: number): number {
  return milliseconds / 1000;
}

// ---------- Spacing -----------------------------------------------------

/**
 * 4px-grid spacing scale. Tailwind's default scale is fine for most
 * use; these constants name semantic levels for components that need
 * to coordinate density.
 */
export const SPACING = {
  /** Inside chat message bubbles. */
  bubblePadding: "px-4 py-3",
  /** Between consecutive messages. */
  messageGap: "gap-3",
  /** Inside the comparison card. */
  cardPadding: "p-6",
  /** Between retailer rows in a comparison. */
  rowGap: "gap-2",
  /** Page-level padding. */
  pageDesktop: "px-8 py-10",
  pageMobile: "px-5 py-6",
} as const;

// ---------- Border radius ----------------------------------------------

export const RADIUS = {
  /** Default container radius (matches `--radius` in globals.css). */
  base: "rounded-[10px]",
  /** Lifted cards (comparison card hero, refinement chips group). */
  large: "rounded-2xl",
  /** Pill-shaped chips, buttons. */
  pill: "rounded-full",
  /** Small inline elements (badges, tags). */
  small: "rounded-md",
} as const;

// ---------- Shadow / elevation -----------------------------------------

/**
 * Dark-mode lift uses background lightness instead of drop-shadow
 * (modern dark-mode pattern). Light-mode lift uses soft shadows.
 *
 * Components should use these tokens instead of `shadow-md` etc. so
 * dark/light mode behave correctly.
 */
export const ELEVATION = {
  /** Resting state. */
  none: "",
  /** Subtle lift on cards. */
  subtle: "shadow-sm dark:bg-secondary",
  /** Hovered / focused interactive surfaces. */
  hover: "shadow-md dark:shadow-none dark:bg-secondary/80",
  /** Active / pressed state. */
  active: "shadow-sm dark:bg-secondary",
} as const;

// ---------- Component constants ----------------------------------------

/**
 * The chat thread maximum width on desktop. Wider than this and the
 * lines become hard to track; narrower and the thread feels cramped.
 * Matches Linear / Claude desktop conventions.
 */
export const CHAT_MAX_WIDTH = "max-w-[720px]";

/** The comparison card maximum width. Slightly wider than chat to
 * accommodate retailer rows + breakdown panel. */
export const COMPARISON_CARD_MAX_WIDTH = "max-w-[760px]";

/** Touch target minimum on mobile (Apple HIG / WCAG). */
export const MIN_TOUCH_TARGET_PX = 44;
